// Completeness heuristic for scraped/imported events. Shared by the scraping
// service (auto-publish decision at creation time) and the admin Review tab
// (why an event still needs a human look), so both surfaces agree on what
// counts as "clearly complete" instead of drifting apart over time.

export interface EventQualityInput {
  title?: string | null
  description?: string | null
  addressText?: string | null
  startsAt?: string | Date | null
}

export interface EventQualityResult {
  missingFields: ('title' | 'description' | 'addressText')[]
  isPastEvent: boolean
  autoPublishable: boolean
}

const REQUIRED_FIELDS = ['title', 'description', 'addressText'] as const

function isBlank(value: string | null | undefined): boolean {
  return !value || value.trim().length === 0
}

/** True if `startsAt` is missing, unparseable, or not in the future — treated as a risk either way. */
function isPastOrUnknown(startsAt: EventQualityInput['startsAt']): boolean {
  if (startsAt == null) return true
  const date = startsAt instanceof Date ? startsAt : new Date(startsAt)
  return Number.isNaN(date.getTime()) || date.getTime() <= Date.now()
}

/** Auto-publishable only when title, description, and address are all present and the event is in the future. */
export function evaluateEventQuality(input: EventQualityInput): EventQualityResult {
  const missingFields = REQUIRED_FIELDS.filter((field) => isBlank(input[field]))
  const isPastEvent = isPastOrUnknown(input.startsAt)

  return {
    missingFields,
    isPastEvent,
    autoPublishable: missingFields.length === 0 && !isPastEvent,
  }
}
